import { numberWithCommas } from "helpers/utils";
import { PREPARE_PER_PAGE } from "./constants";

// "97; 145 ;;145" -> ["97", "145"]
export const parseInvoiceNumbers = raw => {
  if (!raw) return [];
  const seen = new Set();
  const result = [];
  String(raw)
    .split(";")
    .forEach(part => {
      const value = part.trim();
      if (value && !seen.has(value)) {
        seen.add(value);
        result.push(value);
      }
    });
  return result;
};

// Chấp nhận cả tên field của API khách hàng cũ (ten/mst/ma) lẫn API
// statements/buyers (name/tax_code/code)
export const readBuyerName = buyer =>
  (buyer?.ten || buyer?.cty || buyer?.name || buyer?.buyer_name || "").trim();

export const readBuyerTaxCode = buyer =>
  (
    buyer?.mst ||
    buyer?.tax_code ||
    buyer?.buyer_tax_code ||
    buyer?.ma ||
    buyer?.code ||
    ""
  ).trim();

// Bản ghi người mua -> chuỗi keyword API mong đợi: "TÊN (MST)"
export const buildBuyerKeyword = buyer => {
  if (!buyer) return "";
  const name = readBuyerName(buyer);
  const code = readBuyerTaxCode(buyer);
  if (!name) return code;
  // Có API trả sẵn tên đã kèm mã trong ngoặc, đừng nối thêm lần nữa
  if (!code || name.endsWith(`(${code})`)) return name;
  return `${name} (${code})`;
};

export const buildPrepareParams = ({
  keyword,
  taxCode,
  month,
  type,
  invoiceNumbers,
  bucode,
  page = 1,
  perPage = PREPARE_PER_PAGE,
}) => {
  const params = {
    month,
    type,
    page,
    "per-page": perPage,
  };
  // Giới hạn theo chi nhánh/đơn vị đang chọn trên header
  if (bucode) {
    params.bu_code = bucode;
  }
  const trimmedKeyword = (keyword || "").trim();
  if (trimmedKeyword) {
    params.keyword = trimmedKeyword;
  }
  const trimmedTaxCode = (taxCode || "").trim();
  if (trimmedTaxCode) {
    params.tax_code = trimmedTaxCode;
  }
  const numbers = parseInvoiceNumbers(invoiceNumbers);
  if (numbers.length > 0) {
    params.invoice_numbers = numbers;
  }
  return params;
};

// Gom phần phân trang chung cho cả 2 nhánh valid / invalid.
// Backend trả pageCount; nếu thiếu total thì suy ngược từ pageCount * perPage
// để thanh phân trang vẫn hiện đủ số trang.
const normalizePaging = (branch, items, fallbackPerPage) => {
  const perPage = branch.per_page ?? branch.perPage ?? fallbackPerPage;
  const pageCount = branch.pageCount ?? branch.page_count ?? 0;
  const total =
    branch.total ?? (pageCount ? pageCount * perPage : items.length);
  return {
    page: branch.page ?? 1,
    perPage,
    pageCount: pageCount || Math.ceil(total / (perPage || 1)) || 0,
    total,
  };
};

// Backend có thể trả thiếu nhánh nên luôn merge về cấu trúc đầy đủ
export const normalizePrepareResponse = (data, fallbackPerPage) => {
  const perPageDefault = fallbackPerPage || PREPARE_PER_PAGE;
  const valid = data?.valid || {};
  const invalid = data?.invalid || {};
  const validItems = valid.items || [];
  const invalidItems = invalid.items || [];
  return {
    valid: {
      items: validItems,
      ...normalizePaging(valid, validItems, perPageDefault),
      sum_net: valid.sum_net ?? 0,
      sum_vat: valid.sum_vat ?? 0,
      sum_pay: valid.sum_pay ?? 0,
    },
    invalid: {
      items: invalidItems,
      ...normalizePaging(invalid, invalidItems, perPageDefault),
    },
  };
};

export const formatMoney = value => {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0";
  return numberWithCommas(String(num)) || "0";
};

// "2023-09-08 08:26:38" -> "08/09/2023 08:26:38"
export const formatInvoiceDate = value => {
  if (!value) return "";
  const text = String(value);
  const [datePart, timePart = ""] = text.split(" ");
  const segments = datePart.split("-");
  if (segments.length !== 3 || segments.some(seg => !/^\d+$/.test(seg)))
    return text;
  const [year, month, day] = segments;
  return timePart
    ? `${day}/${month}/${year} ${timePart}`
    : `${day}/${month}/${year}`;
};

export const toggleSelectedId = (selectedIds, id, isSelect) => {
  if (isSelect) {
    return selectedIds.includes(id) ? selectedIds : [...selectedIds, id];
  }
  return selectedIds.filter(item => item !== id);
};

// rows là các dòng của trang hiện tại; lựa chọn ở trang khác phải giữ nguyên
export const toggleSelectedAll = (selectedIds, rows, isSelect) => {
  const pageIds = (rows || []).map(row => row.id);
  if (isSelect) {
    return [...selectedIds, ...pageIds.filter(id => !selectedIds.includes(id))];
  }
  return selectedIds.filter(id => !pageIds.includes(id));
};

// "2023", "09" -> "2023-09" (định dạng param month của API)
export const buildMonthValue = (year, month) => {
  if (!year || !month) return "";
  return `${year}-${String(month).padStart(2, "0")}`;
};

// "2023-09" -> { year: "2023", month: "09" }; giá trị lạ trả về rỗng
export const parseMonthValue = value => {
  const segments = String(value || "").split("-");
  if (segments.length !== 2 || !segments.every(part => /^\d+$/.test(part))) {
    return { year: "", month: "" };
  }
  return { year: segments[0], month: segments[1].padStart(2, "0") };
};

// "2023-09" -> "Tháng 09/2023" để hiện lại điều kiện đã tìm
export const formatMonthLabel = value => {
  const { year, month } = parseMonthValue(value);
  if (!year || !month) return "";
  return `Tháng ${month}/${year}`;
};

// Danh sách năm cho ô chọn, mới nhất ở trên
export const buildYearOptions = (currentYear, before, after) => {
  const years = [];
  for (
    let year = currentYear + after;
    year >= currentYear - before;
    year -= 1
  ) {
    years.push(String(year));
  }
  return years;
};

// Bảng báo lên (trang, số dòng/trang) sau mỗi thao tác phân trang.
// Đổi số dòng/trang thì số trang đã khác hẳn nên phải quay về trang 1,
// giữ nguyên trang cũ dễ rơi ra ngoài phạm vi và trả về danh sách rỗng.
export const resolvePagingChange = (current, page, sizePerPage) => {
  const perPage = sizePerPage || current.perPage;
  return {
    page: perPage !== current.perPage ? 1 : page,
    perPage,
  };
};

// Email người mua để gửi link ký. Gom từ mọi nguồn BE có thể trả:
// esign_email (email đã dùng lần trước) để lên đầu làm mặc định, rồi tới
// danh sách emails; nhận cả mảng lẫn chuỗi ngăn cách bởi , hoặc ;
export const readBuyerEmails = buyer => {
  const sources = [
    buyer?.esign_email,
    buyer?.emails,
    buyer?.email,
    buyer?.dctdtu,
  ];
  const seen = new Set();
  const result = [];
  sources.forEach(source => {
    if (!source) return;
    const items = Array.isArray(source) ? source : String(source).split(/[,;]/);
    items.forEach(item => {
      const email = String(item ?? "").trim();
      if (!email || seen.has(email)) return;
      seen.add(email);
      result.push(email);
    });
  });
  return result;
};

export const isValidEmail = value =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
